import { ApolloError, QueryHookOptions, useQuery } from '@apollo/client';
import {
    GetCustomerDetailsDocument,
    GQLGetCustomerDetailsQuery,
    GQLGetCustomerDetailsQueryVariables,
} from '@upheal/graphql/definitions/billing/GetCustomerDetails.generated';
import { captureException } from '@upheal/shared/utils/sentry';

type Options = Omit<
    QueryHookOptions<
        GQLGetCustomerDetailsQuery,
        GQLGetCustomerDetailsQueryVariables
    >,
    'onError'
> & {
    onError?: (error: ApolloError) => void;
};

/**
 * Low-level query hook for GetCustomerDetails.
 * Always captures errors to Sentry; callers can provide additional onError handling (e.g. snackbars).
 *
 * Future direction: decompose into focused hooks like useCurrentSubscriptionQuery,
 * usePremiumFeaturesQuery, useCurrentUsageQuery, etc.
 */
export const useCustomerDetailsQuery = (options?: Options) => {
    const { onError: callerOnError, ...restOptions } = options ?? {};

    return useQuery(GetCustomerDetailsDocument, {
        notifyOnNetworkStatusChange: true,
        ...restOptions,
        onError: (error) => {
            captureException('Failed to load subscription data', { error });
            callerOnError?.(error);
        },
    });
};
